import { LayoutType } from "./LayoutType";
import { AspectRatio } from "./AspectRatio";
import { MediaRole } from "./MediaRole";
import { MediaObject } from './media';

// Common aspect ratios
const LANDSCAPE_RATIO = 16 / 9;
const PORTRAIT_RATIO = 9 / 16;
const SQUARE_RATIO = 1;

// Helper functions for common layout calculations
const calculateCenteredBlock = (width: number, height: number, padding: number) => {
    // Use the smallest pixel padding
    const pixelPadding = Math.min(padding * width, padding * height);
    const xPx = pixelPadding;
    const yPx = pixelPadding;
    const wPx = width - 2 * pixelPadding;
    const hPx = height - 2 * pixelPadding;
    return {
        id: 'main',
        x: xPx / width,
        y: yPx / height,
        width: wPx / width,
        height: hPx / height,
        roles: ['main'] as MediaRole[],
        media: null as MediaObject | null
    };
};

const calculateSquareBlock = ( padding: number, width: number, height: number) => {
    // Use the smallest pixel padding
    const pixelPadding = Math.min(padding * width, padding * height);
    const paddedWidth = width - 2 * pixelPadding;
    const paddedHeight = height - 2 * pixelPadding;
    const blockSize = Math.min(paddedWidth, paddedHeight);
    const xPx = pixelPadding + (paddedWidth - blockSize) / 2;
    const yPx = pixelPadding + (paddedHeight - blockSize) / 2;
    return {
        id: 'main',
        x: xPx / width,
        y: yPx / height,
        width: blockSize / width,
        height: blockSize / height,
        roles: ['main'] as MediaRole[],
        media: null as MediaObject | null
    };
};

const calculateSplitBlocks = (padding: number, count: number, width: number, height: number) => {
    const blocks = [];
    // Calculate pixel padding as the smallest of padding * width or padding * height
    const pixelPadding = Math.min(padding * width, padding * height);
    // Calculate available space in pixels
    const availableWidth = width - 2 * pixelPadding;
    const availableHeight = height - 2 * pixelPadding;
    // Determine if vertical split is better based on aspect ratio
    const isVertical = availableHeight / availableWidth > 1;
    // Calculate block size and spacing in pixels
    const blockSpacing = pixelPadding;
    const blockSize = (isVertical
        ? (availableHeight - (blockSpacing * (count - 1))) / count
        : (availableWidth - (blockSpacing * (count - 1))) / count);
    for (let i = 0; i < count; i++) {
        // Calculate pixel positions and sizes
        const xPx = isVertical ? pixelPadding : pixelPadding + i * (blockSize + blockSpacing);
        const yPx = isVertical ? pixelPadding + i * (blockSize + blockSpacing) : pixelPadding;
        const wPx = isVertical ? availableWidth : blockSize;
        const hPx = isVertical ? blockSize : availableHeight;
        // Normalize positions and sizes
        const x = xPx / width;
        const y = yPx / height;
        const w = wPx / width;
        const h = hPx / height;
        blocks.push({
            id: `split-${i}`,
            x,
            y,
            width: w,
            height: h,
            roles: ['main'] as MediaRole[],
            media: null as MediaObject | null
        });
    }
    return blocks;
};

const calculateGridBlocks = (padding: number, count: number, width: number, height: number) => {
    // Use the smallest pixel padding
    const pixelPadding = Math.min(padding * width, padding * height);
    // Calculate number of columns and rows
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    // Calculate available space in pixels
    const availableWidth = width - 2 * pixelPadding;
    const availableHeight = height - 2 * pixelPadding;
    // Calculate block size in pixels
    const blockWidthPx = (availableWidth - (cols - 1) * pixelPadding) / cols;
    const blockHeightPx = (availableHeight - (rows - 1) * pixelPadding) / rows;
    const blocks: LayoutBlock[] = [];
    for (let i = 0; i < count; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const xPx = pixelPadding + col * (blockWidthPx + pixelPadding);
        const yPx = pixelPadding + row * (blockHeightPx + pixelPadding);
        blocks.push({
            id: `grid-${i}`,
            x: xPx / width,
            y: yPx / height,
            width: blockWidthPx / width,
            height: blockHeightPx / height,
            roles: ['main'] as MediaRole[],
            media: null as MediaObject | null
        });
    }
    return blocks;
};

const calculateScreenshareLayout = (padding: number, width: number, height: number) => {
    const pixelPadding = Math.min(padding * width, padding * height);
    const availableWidth = width - 2 * pixelPadding;
    const availableHeight = height - 2 * pixelPadding;
    const cameraFraction = 0.25; // Camera block is 1/4 the smallest available dimension
    const cameraSize = Math.min(availableWidth, availableHeight) * cameraFraction;
    const cameraX = pixelPadding + availableWidth - cameraSize;
    const cameraY = pixelPadding + availableHeight - cameraSize;
    return [
        {
            id: 'screen',
            x: pixelPadding / width,
            y: pixelPadding / height,
            width: availableWidth / width,
            height: availableHeight / height,
            roles: ['screen'] as MediaRole[],
            media: null as MediaObject | null
        },
        {
            id: 'camera',
            x: cameraX / width,
            y: cameraY / height,
            width: cameraSize / width,
            height: cameraSize / height,
            roles: ['main','speaker1'] as MediaRole[],
            media: null as MediaObject | null
        }
    ];
};

const calculateGameplayLayout = (padding: number, width: number, height: number) => {
    // Calculate pixel padding as the smallest of padding * width or padding * height
    const pixelPadding = Math.min(padding * width, padding * height);
    // Calculate available space in pixels
    const availableWidth = width - 2 * pixelPadding;
    const availableHeight = height - 2 * pixelPadding;
    // Determine if vertical layout is better based on aspect ratio
    const isVertical = availableHeight / availableWidth > 1;

    if (isVertical) {
        // Vertical layout (stacked)
        return [
            {
                id: 'main',
                x: pixelPadding / width,
                y: pixelPadding / height,
                width: availableWidth / width,
                height: (availableHeight * 0.7) / height,
                roles: ['main', 'speaker1'] as MediaRole[],
                media: null as MediaObject | null
            },
            {
                id: 'gameplay',
                x: pixelPadding / width,
                y: (pixelPadding + availableHeight * 0.7 + pixelPadding) / height,
                width: availableWidth / width,
                height: (availableHeight * 0.3 - pixelPadding) / height,
                roles: ['screen'] as MediaRole[],
                media: null as MediaObject | null
            }
        ];
    } else {
        // Horizontal layout (side by side)
        return [
            {
                id: 'main',
                x: pixelPadding / width,
                y: pixelPadding / height,
                width: (availableWidth * 0.7) / width,
                height: availableHeight / height,
                roles: ['main', 'speaker1'] as MediaRole[],
                media: null as MediaObject | null
            },
            {
                id: 'gameplay',
                x: (pixelPadding + availableWidth * 0.7 + pixelPadding) / width,
                y: pixelPadding / height,
                width: (availableWidth * 0.3 - pixelPadding) / width,
                height: availableHeight / height,
                roles: ['screen'] as MediaRole[],
                media: null as MediaObject | null
            }
        ];
    }
};

const calculateSpeakerMediaLayout = (padding: number, width: number, height: number) => {
    // Calculate pixel padding as the smallest of padding * width or padding * height
    const pixelPadding = Math.min(padding * width, padding * height);
    // Calculate available space in pixels
    const availableWidth = width - 2 * pixelPadding;
    const availableHeight = height - 2 * pixelPadding;
    // Determine if vertical layout is better based on aspect ratio
    const isVertical = availableHeight / availableWidth > 1;

    if (isVertical) {
        // Vertical layout (stacked)
        return [
            {
                id: 'speaker',
                x: pixelPadding / width,
                y: pixelPadding / height,
                width: availableWidth / width,
                height: (availableHeight * 0.3) / height,
                roles: ['speaker1','main'] as MediaRole[],
                media: null as MediaObject | null
            },
            {
                id: 'media',
                x: pixelPadding / width,
                y: (pixelPadding + availableHeight * 0.3 + pixelPadding) / height,
                width: availableWidth / width,
                height: (availableHeight * 0.7 - pixelPadding) / height,
                roles: ['main', 'screen'] as MediaRole[],
                media: null as MediaObject | null
            }
        ];
    } else {
        // Horizontal layout (side by side)
        return [
            {
                id: 'speaker',
                x: pixelPadding / width,
                y: pixelPadding / height,
                width: (availableWidth * 0.3) / width,
                height: availableHeight / height,
                roles: ['speaker1','main'] as MediaRole[],
                media: null as MediaObject | null
            },
            {
                id: 'media',
                x: (pixelPadding + availableWidth * 0.3 + pixelPadding) / width,
                y: pixelPadding / height,
                width: (availableWidth * 0.7 - pixelPadding) / width,
                height: availableHeight / height,
                roles: ['main', 'screen'] as MediaRole[],
                media: null as MediaObject | null
            }
        ];
    }
};

const calculateOverlayLayout = (
    padding: number,
    width: number,
    height: number,
    overlaySize: 'half' | 'third' | 'square',
    verticalPosition: 'top' | 'bottom' = 'top',
    horizontalPosition: 'left' | 'right' = 'right',
    overlayHasPadding: boolean = true
) => {
    const pixelPadding = Math.min(padding * width, padding * height);
    const availableWidth = width - 2 * pixelPadding;
    const availableHeight = height - 2 * pixelPadding;
    const isVertical = availableHeight / availableWidth > 1;

    // Overlay size as a fraction
    const sizeFraction = {
        half: 0.5,
        third: 1 / 3,
        square: 1 / 2
    }[overlaySize];

    const overlayMargin = overlayHasPadding ? pixelPadding : 0;

    if (isVertical) {
        if (overlaySize === 'square') {
            // Square overlay: width and height are the same, fit within availableWidth and overlayHeight
            const maxSquare = Math.min(availableWidth - 2 * overlayMargin, availableHeight - 2 * overlayMargin);
            const overlayY = verticalPosition === 'top'
                ? pixelPadding + overlayMargin
                : pixelPadding + availableHeight - maxSquare - overlayMargin;
            const overlayX = pixelPadding + (availableWidth - maxSquare) / 2;
            return [
                {
                    id: 'speaker',
                    x: pixelPadding / width,
                    y: pixelPadding / height,
                    width: availableWidth / width,
                    height: availableHeight / height,
                    roles: ['speaker1','main'] as MediaRole[],
                    media: null as MediaObject | null
                },
                {
                    id: 'overlay',
                    x: overlayX / width,
                    y: overlayY / height,
                    width: maxSquare / width,
                    height: maxSquare / height,
                    roles: ['overlay', 'screen'] as MediaRole[],
                    media: null as MediaObject | null
                }
            ];
        } else {
            // Overlay takes full width minus margin, height is fraction
            const overlayHeight = availableHeight * sizeFraction;
            const overlayY = verticalPosition === 'top'
                ? pixelPadding
                : pixelPadding + availableHeight - overlayHeight;
            return [
                {
                    id: 'speaker',
                    x: pixelPadding / width,
                    y: pixelPadding / height,
                    width: availableWidth / width,
                    height: availableHeight / height,
                    roles: ['speaker1','main'] as MediaRole[],
                    media: null as MediaObject | null
                },
                {
                    id: 'overlay',
                    x: (pixelPadding + overlayMargin) / width,
                    y: overlayY / height,
                    width: (availableWidth - 2 * overlayMargin) / width,
                    height: (overlayHeight - overlayMargin) / height,
                    roles: ['overlay', 'screen'] as MediaRole[],
                    media: null as MediaObject | null
                }
            ];
        }
    } else {
        if (overlaySize === 'square') {
            // Square overlay: width and height are the same, fit within availableHeight and overlayWidth
            const maxSquare = Math.min(availableHeight - 2 * overlayMargin, availableWidth - 2 * overlayMargin);
            const overlayX = horizontalPosition === 'right'
                ? pixelPadding + availableWidth - maxSquare - overlayMargin
                : pixelPadding + overlayMargin;
            const overlayY = pixelPadding + (availableHeight - maxSquare) / 2;
            return [
                {
                    id: 'speaker',
                    x: pixelPadding / width,
                    y: pixelPadding / height,
                    width: availableWidth / width,
                    height: availableHeight / height,
                    roles: ['speaker1','main'] as MediaRole[],
                    media: null as MediaObject | null
                },
                {
                    id: 'overlay',
                    x: overlayX / width,
                    y: overlayY / height,
                    width: maxSquare / width,
                    height: maxSquare / height,
                    roles: ['overlay', 'screen'] as MediaRole[],
                    media: null as MediaObject | null
                }
            ];
        } else {
            // Overlay takes full height minus margin, width is fraction
            const overlayWidth = availableWidth * sizeFraction;
            const overlayX = horizontalPosition === 'right'
                ? pixelPadding + availableWidth - overlayWidth
                : pixelPadding;
            return [
                {
                    id: 'speaker',
                    x: pixelPadding / width,
                    y: pixelPadding / height,
                    width: availableWidth / width,
                    height: availableHeight / height,
                    roles: ['speaker1','main'] as MediaRole[],
                    media: null as MediaObject | null
                },
                {
                    id: 'overlay',
                    x: overlayX / width,
                    y: (pixelPadding + overlayMargin) / height,
                    width: (overlayWidth - overlayMargin) / width,
                    height: (availableHeight - 2 * overlayMargin) / height,
                    roles: ['overlay', 'screen'] as MediaRole[],
                    media: null as MediaObject | null
                }
            ];
        }
    }
};

const calculatePortraitBlock = (padding: number,  width: number, height: number) => {
    // Use the smallest pixel padding
    const pixelPadding = Math.min(padding * width, padding * height);
    const paddedWidth = width - 2 * pixelPadding;
    const paddedHeight = height - 2 * pixelPadding;
    const targetRatio = 9 / 16;
    let blockWidth: number;
    let blockHeight: number;
    if (paddedWidth / paddedHeight > targetRatio) {
        blockHeight = paddedHeight;
        blockWidth = blockHeight * targetRatio;
    } else {
        blockWidth = paddedWidth;
        blockHeight = blockWidth / targetRatio;
    }
    const xPx = pixelPadding + (paddedWidth - blockWidth) / 2;
    const yPx = pixelPadding + (paddedHeight - blockHeight) / 2;
    return {
        id: 'main',
        x: xPx / width,
        y: yPx / height,
        width: blockWidth / width,
        height: blockHeight / height,
        roles: ['main'] as MediaRole[],
        media: null as MediaObject | null
    };
};

const calculateLandscapeBlock = (padding: number, width: number, height: number) => {
    // Use the smallest pixel padding
    const pixelPadding = Math.min(padding * width, padding * height);
    const paddedWidth = width - 2 * pixelPadding;
    const paddedHeight = height - 2 * pixelPadding;
    const targetRatio = 16 / 9;
    let blockWidth: number;
    let blockHeight: number;
    if (paddedWidth / paddedHeight > targetRatio) {
        blockHeight = paddedHeight;
        blockWidth = blockHeight * targetRatio;
    } else {
        blockWidth = paddedWidth;
        blockHeight = blockWidth / targetRatio;
    }
    const xPx = pixelPadding + (paddedWidth - blockWidth) / 2;
    const yPx = pixelPadding + (paddedHeight - blockHeight) / 2;
    return {
        id: 'main',
        x: xPx / width,
        y: yPx / height,
        width: blockWidth / width,
        height: blockHeight / height,
        roles: ['main'] as MediaRole[],
        media: null as MediaObject | null
    };
};

export interface LayoutBlock {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    roles: MediaRole[];
    media: MediaObject | null;
}

export const resolveLayout = (
    layout: LayoutType,
    inputs: MediaObject[],
    padding: number,
    width: number,
    height: number
): LayoutBlock[] => {


    let blocks: LayoutBlock[] = [];

    switch (layout) {
        case 'default':
        case 'fullscreen':
            blocks = [calculateCenteredBlock(width, height, padding)];
            break;
        case 'landscape':
            blocks = [calculateLandscapeBlock(padding, width, height)];
            break;
        case 'portrait':
            blocks = [calculatePortraitBlock(padding, width, height)];
            break;
        case 'square':
            blocks = [calculateSquareBlock( padding, width, height)];
            break;
        case 'split':
            blocks = calculateSplitBlocks(padding, 2, width, height);
            break;
        case 'split-vertical':
            blocks = calculateSplitBlocks(padding, 2, width, height);
            break;
        case 'split-horizontal':
            blocks = calculateSplitBlocks(padding, 2, width, height);
            break;
        case '2-split':
            blocks = calculateSplitBlocks(padding, 2, width, height);
            break;
        case '3-split':
            blocks = calculateSplitBlocks(padding, 3, width, height);
            break;
        case '4-split':
            blocks = calculateGridBlocks(padding, 4, width, height);
            break;
        case 'grid':
            blocks = calculateGridBlocks(padding, 4, width, height);
            break;
        case 'screenshare':
            blocks = calculateScreenshareLayout(padding, width, height);
            break;
        case 'gameplay':
            blocks = calculateGameplayLayout(padding, width, height);
            break;
        case '1-third-speaker-2-third-media':
            blocks = calculateSpeakerMediaLayout(padding, width, height);
            break;
        case 'speaker-with-overlay-half-screen':
            blocks = calculateOverlayLayout(padding, width, height, 'half');
            break;
        case 'speaker-with-overlay-1-third':
            blocks = calculateOverlayLayout(padding, width, height, 'third');
            break;
        case 'speaker-with-overlay-square':
            blocks = calculateOverlayLayout(padding, width, height, 'square');
            break;
        default:
            blocks = [calculateCenteredBlock(width, height, padding)];
    }

    // Map media objects to their layout blocks based on roles
    blocks.forEach(block => {
        const blockMedia = inputs.find(media => block.roles.includes(media.role));
        if (blockMedia) {
            block.media = blockMedia;
        }
    });

    return blocks;
};